/** The Pixi side of the bitmap font.
 *
 * A label is one `Graphics` holding two batches of axis-aligned rectangles: the
 * keyline first, the glyph bodies over it. There is no texture and no font
 * asset, so there is nothing to filter, nothing to upload and nothing that can
 * be resampled - which is the whole point. The battle `Application` is created
 * with `antialias: false`, so a rectangle edge that lands between two device
 * pixels resolves to one of them instead of blending, and the drawn numeral
 * carries exactly the two colours it was given however the arena is scaled.
 *
 * Everything is laid out in whole glyph pixels and multiplied by a whole
 * `scale`. Nothing here ever produces a fractional local coordinate.
 *
 * A whole local scale is not by itself a whole *magnification*, because the
 * arena above these labels is scaled to fill the window - see
 * `snapMagnification`, which is what pins the composite to a whole number.
 */

import { Container, Graphics } from "pixi.js";
import type { FaceId } from "./glyphs.js";
import { FACES } from "./glyphs.js";
import { MIN_DEVICE_CAP, holdToFloor } from "./devicePixels.js";
import { KEYLINE, anchorOffset, layoutBitmapText, lineHeightOf, measureBitmapText } from "./layout.js";
import type { BitmapTextStyle } from "./layout.js";

export interface BitmapLabelStyle {
  /** Colour of the glyph body. */
  readonly fill: number;
  /**
   * Which of the four faces. `"hud"` (cap 7) by default, `"text"` (cap 9) for
   * a panel caption, `"tall"` (cap 11 at the text face's widths) and
   * `"display"` (cap 11, 2px stems) for a heading.
   */
  readonly face?: FaceId;
  /**
   * Draw the 1px dark keyline. **Off by default**, and that is the point: see
   * the header of `layout.ts`. Turn it on only for a string drawn over art this
   * build does not control - a damage floater, a morale banner, a count printed
   * onto a creature. A label on a plate the build painted does not need one and
   * measured 2.4-2.6x more keyline than body when it had one.
   */
  readonly keyline?: boolean;
  /** Colour of the keyline, when one is drawn. Defaults to near-black. */
  readonly outline?: number;
  /** Keyline opacity. Defaults to fully opaque - a translucent keyline is not one. */
  readonly outlineAlpha?: number;
  /** Whole-number magnification of the glyph pixels. Defaults to 1. */
  readonly scale?: number;
  /**
   * Hold the reading to `MIN_DEVICE_CAP` device pixels of cap by promoting
   * the FACE - never the magnification, because an arena label is measured
   * into a box it may not outgrow. **On by default.** A seven-row HUD caption
   * that lands on one device pixel per texel (the 1080p arena at 1.5 floors a
   * scale-1 label to 1) is stamped in the eleven-row display face instead;
   * see `heldFace`. Off only for a mark whose box is tighter than the display
   * face's width and whose size is the design.
   */
  readonly floor?: boolean;
  /**
   * Scale of the containers this label will be drawn under - `arenaScale` for
   * anything in the battle screen. Defaults to 1. See `snapMagnification`: it
   * is the difference between a glyph texel being worth a whole number of
   * device pixels and being worth 2.134 of them.
   */
  readonly deviceScale?: number;
  /**
   * The tallest box, in the parent's units, this label may stand in once it
   * is held to the floor - the count plaque passes its field height. The
   * floor promotes a face by cap and the plaque cannot grow with it: at 1080p
   * and 80% zoom the arena is 1.2 device px per arena px, the plaque's field
   * 12 device rows, and the fifteen-row caption face the 1080p floor wants is
   * three rows of numeral drawn over the plate. With a fit the promotion
   * stops at the tallest face of the family that stands inside the box
   * (`fitFace`); with none the box is assumed to grow around the label, which
   * is what a console line and a floater do. A fit never draws a label
   * smaller than it asked for, and never smaller than the floor would have
   * when nothing fits at all.
   */
  readonly fitHeight?: number;
}

/** What a label ends up drawing at, once the ambient scale is accounted for. */
export interface SnappedMagnification {
  /** Whole local scale, as asked for. */
  readonly nominal: number;
  /** Whole device pixels per glyph texel. Always an integer >= 1. */
  readonly magnification: number;
  /** Local scale to apply so the composite lands on `magnification`. */
  readonly compensation: number;
}

/**
 * The whole magnification a label may use under an ambient `deviceScale`, and
 * the local scale that pins it there.
 *
 * The arena is authored at 1280x720 and scaled to fill the window - 1.5 at
 * 1920x1080, 1.0672 at 1366x768 - so a label authored at scale 2 asks for 3.0
 * device pixels per glyph texel at one viewport and 2.134 at the other. 2.134
 * cannot be paid: the rasteriser spends two device rows on six of the seven
 * glyph rows and three on the seventh, so one scanline inside one numeral is
 * doubled and two strokes of the same digit come out different widths. Measured
 * on the shipped 1366x768 shot before this existed, the stack card's count `42`
 * stood 15 rows tall in the pattern 2,2,2,2,3,2,2, with a 2px stem on the `4`
 * beside a 3px diagonal on the `2`; at 1920x1080 the same label was a clean 21.
 *
 * So the magnification is floored to a whole number and the label is scaled by
 * the reciprocal of what that cost. Once every texel is worth the same whole
 * number of device pixels, the run boundaries sit at a fixed integer stride
 * from the label's origin, and every stroke rounds the same way whatever
 * fraction that origin lands on - which is the property that makes the same
 * digit rasterise identically in every label on the screen.
 *
 * Floored, never rounded. A call site measures a label into a box it owns - the
 * stack card's gutters and its two stacked rows, the 23px count plaque - using
 * the nominal size; rounding 1.5 up to 2 would grow a four-digit count past the
 * plaque and push the card's caption row into its figure row. A label may be
 * drawn smaller than it asked for. It may never be drawn larger than the space
 * it was measured into.
 */
export function snapMagnification(scale: number, deviceScale: number): SnappedMagnification {
  const nominal = Math.max(1, Math.round(scale));
  const ambient = deviceScale > 0 ? deviceScale : 1;
  const want = nominal * ambient;
  // The epsilon is for the exact cases: 1.5 * 2 must floor to 3, and floating
  // point does not always hand back exactly 3.
  const magnification = Math.max(1, Math.floor(want + 1e-9));
  return { nominal, magnification, compensation: magnification / want };
}

/** The sans family in ascending cap order; a label is promoted along it. The
 *  tall face sits before the display face at the same cap, so the promotion
 *  a floor forces takes the narrower of the two. */
const SANS_FAMILY: readonly FaceId[] = ["hud", "text", "tall", "display", "caption"];

/** The condensed roman family, the arena's own: the three caps the floor
 *  lands on (11 on a 768-tall window, 14 for a label that asked for two
 *  pixels per texel there, 15 on a 1080-tall one). */
const ROMAN_FAMILY: readonly FaceId[] = ["roman", "romanDisplay", "romanCaption"];

/** The families, in the order a face is looked for. A promotion never crosses
 *  from one to the other: a console line that changed letterform when the
 *  window grew would be a different console. */
const FAMILIES: readonly (readonly FaceId[])[] = [SANS_FAMILY, ROMAN_FAMILY];

/** The family `id` belongs to, and its index in it. Index -1 for a face in no
 *  family, which is never promoted. */
function familyOf(id: FaceId): { readonly family: readonly FaceId[]; readonly index: number } {
  for (const family of FAMILIES) {
    const index = family.indexOf(id);
    if (index !== -1) return { family, index };
  }
  return { family: SANS_FAMILY, index: -1 };
}

/**
 * The cap floor every label under this renderer is held to, in device
 * pixels. The arena's labels are built in `ui.ts`, `stacks.ts` and `fx.ts`
 * with no view of the window; the presenter, which does have one, sets this
 * from `deviceCapFloor(cssHeight * resolution)` on every resize, BEFORE it
 * re-pins the labels' device scale, so a label built afterwards and a label
 * re-pinned afterwards both read the floor of the window they are drawn in.
 * `MIN_DEVICE_CAP` until a presenter says otherwise - the laptop floor, and
 * what every test measures against.
 */
let ambientCapFloor = MIN_DEVICE_CAP;

/** Set the cap floor for every label built or re-pinned from now on. */
export function setAmbientCapFloor(floor: number): void {
  ambientCapFloor = floor >= MIN_DEVICE_CAP ? floor : MIN_DEVICE_CAP;
}

/**
 * A numeral may stand one device row over its fit: the plate's own rule row,
 * which the reference's nine-row digit touches inside a ten-row field.
 */
const FIT_TOLERANCE = 1;

/**
 * The face and whole device magnification a label of `face` lands on: held
 * to the floor along the family (`holdToFloor`), and then, when the label
 * named a `fitDevice` (its box in device pixels), pulled back to the tallest
 * face of the family from the asked one up that stands inside it. The pull
 * back only happens when the promoted face does not fit; when NO face fits
 * the promoted one stands, because the floor is the least a reading may be.
 */
function resolveHeld(
  face: FaceId | undefined, magnification: number, floor: boolean, floorPx: number, fitDevice: number | undefined,
): { readonly face: FaceId; readonly magnification: number } {
  const id = face ?? "hud";
  const { family, index } = familyOf(id);
  if (!floor || index === -1) return { face: id, magnification };
  const caps = family.map((f) => FACES[f].capH);
  // `holdToFloor` may also step the magnification; only the face is taken,
  // and only when the promotion is found at a magnification the label can
  // draw at: the label's own, or 1 of a larger face for a label at 2, which
  // the returned magnification reports so the label can re-pin itself to it.
  const held = holdToFloor(caps, index, magnification, 1, floorPx);
  const promoted = held.magnification <= magnification
    ? { face: family[held.face] ?? id, magnification: Math.min(magnification, held.magnification) }
    : { face: id, magnification };
  if (fitDevice === undefined || !(fitDevice > 0)) return promoted;
  if (FACES[promoted.face].capH * promoted.magnification <= fitDevice + FIT_TOLERANCE) return promoted;
  const fit = fitFace(family, index, magnification, fitDevice);
  return fit === null ? promoted : { face: fit, magnification };
}

/** The tallest face of the family, from `index` up, whose cap at
 *  `magnification` stands inside `fitDevice` (plus the tolerance); null when
 *  even the asked face does not. */
function fitFace(
  family: readonly FaceId[], index: number, magnification: number, fitDevice: number,
): FaceId | null {
  let best: FaceId | null = null;
  let bestCap = 0;
  for (let f = index; f < family.length; f++) {
    const id = family[f]!;
    const cap = FACES[id].capH * magnification;
    // Strictly taller to replace: at one cap the earlier face of the family
    // stands (`tall` before `display`), the narrower one, as the floor's own
    // promotion prefers.
    if (cap <= fitDevice + FIT_TOLERANCE && cap > bestCap) { best = id; bestCap = cap; }
  }
  return best;
}

/**
 * The face a label of `face` is actually stamped in at `magnification` device
 * pixels per texel, once held to the floor.
 *
 * The arena cannot step a label's magnification - its plaques, cards and
 * console are laid out in arena pixels around the nominal size - so the only
 * move it has is along the family: HUD (7) to text (9) to display (11), the
 * least face that clears `MIN_DEVICE_CAP` at this magnification. At 1080p the
 * arena scale is 1.5 and a scale-1 label floors to 1 device pixel per texel,
 * so every scale-1 HUD or text reading (the ATTACKER / DEFENDER role caption,
 * the stack card's detail lines) goes to the tall face at 11 device rows on
 * a laptop window and the caption face at 15 on a 1080p one; a scale-2
 * label is 14 or more and is left alone at 11, and goes to the caption face
 * at magnification 1 - the same 15 rows, drawn crisp - under 15.
 *
 * `floorPx` is the floor in device pixels; the ambient one by default.
 */
export function heldFace(
  face: FaceId | undefined, magnification: number, floor = true, floorPx = ambientCapFloor, fitDevice?: number,
): FaceId {
  return resolveHeld(face, magnification, floor, floorPx, fitDevice).face;
}

/**
 * The whole device magnification a label of `face` lands on once held: its
 * own, or a smaller one when the floor is cleared by a larger face drawn at
 * fewer device pixels per texel (a HUD label at 2 becoming the caption face
 * at 1). Never larger than `magnification`.
 */
export function heldMagnification(
  face: FaceId | undefined, magnification: number, floor = true, floorPx = ambientCapFloor, fitDevice?: number,
): number {
  return resolveHeld(face, magnification, floor, floorPx, fitDevice).magnification;
}

/**
 * `snapped`, re-pinned to the magnification the floor lands the label on.
 * Usually the same; under a floor of 15 a HUD label at 2 device pixels per
 * texel (14 rows) is stamped as the caption face at 1, and its compensation
 * follows so the composite lands on exactly that one device pixel per texel.
 */
function pinToFloor(
  snapped: SnappedMagnification, face: FaceId | undefined, floor: boolean, fitDevice?: number,
): SnappedMagnification {
  const magnification = heldMagnification(face, snapped.magnification, floor, ambientCapFloor, fitDevice);
  if (magnification === snapped.magnification) return snapped;
  return {
    nominal: snapped.nominal,
    magnification,
    compensation: snapped.compensation * (magnification / snapped.magnification),
  };
}

/**
 * A short run of bitmap text - a count, a reading, a plaque label.
 *
 * `setAnchor` matches `Text.anchor`: (0,0) puts the box's top-left at the
 * label's position, (0.5,0.5) centres it, (1,0) right-aligns it. The offset it
 * produces is rounded, so an odd-width box still lands on a whole pixel.
 */
export class BitmapLabel extends Container {
  private readonly g = new Graphics();
  private readonly fillColor: number;
  private readonly outlineColor: number;
  private readonly outlineAlpha: number;
  private textStyle: BitmapTextStyle;
  private readonly askedFace: FaceId | undefined;
  private readonly floor: boolean;
  private readonly fitHeight: number | undefined;
  private readonly pixelScale: number;
  private snapped: SnappedMagnification;
  private value = "";
  private anchorX = 0;
  private anchorY = 0;
  private boxW = 0;
  private boxH = 0;

  constructor(style: BitmapLabelStyle, text = "") {
    super();
    this.fillColor = style.fill;
    this.outlineColor = style.outline ?? 0x0b0805;
    this.outlineAlpha = style.outlineAlpha ?? 1;
    this.askedFace = style.face;
    this.floor = style.floor !== false;
    this.fitHeight = style.fitHeight;
    const fit = this.fitDevice(style.deviceScale ?? 1);
    this.snapped = pinToFloor(
      snapMagnification(style.scale ?? 1, style.deviceScale ?? 1), this.askedFace, this.floor, fit,
    );
    this.textStyle = {
      face: heldFace(this.askedFace, this.snapped.magnification, this.floor, ambientCapFloor, fit),
      keyline: style.keyline === true ? KEYLINE : 0,
    };
    this.pixelScale = this.snapped.nominal;
    this.scale.set(this.snapped.compensation);
    this.addChild(this.g);
    this.text = text;
  }

  get text(): string {
    return this.value;
  }

  set text(next: string) {
    if (next === this.value && this.boxH !== 0) return;
    this.value = next;
    this.redraw();
  }

  /** Fractional anchor within the drawn box, as `Text.anchor`. */
  setAnchor(x: number, y: number): void {
    this.anchorX = x;
    this.anchorY = y;
    this.place();
  }

  /**
   * Re-pin the magnification to a new ambient scale. The glyph geometry does not
   * change - only how much of a device pixel one of its texels is worth - so a
   * label that outlives a window resize is re-pinned rather than rebuilt.
   */
  setDeviceScale(deviceScale: number): void {
    const fit = this.fitDevice(deviceScale);
    const next = pinToFloor(snapMagnification(this.pixelScale, deviceScale), this.askedFace, this.floor, fit);
    // The face is resolved even when the magnification did not move: the
    // presenter sets the window's cap floor (`setAmbientCapFloor`) and then
    // re-pins every label, and a label built at one floor and re-pinned at
    // another lands on the same magnification with a different face. It used
    // to return early on an unchanged compensation, which left every count
    // plaque built before the first resize at the laptop floor's eleven-row
    // face on a 1080p window whose floor was fifteen.
    const face = heldFace(this.askedFace, next.magnification, this.floor, ambientCapFloor, fit);
    if (next.compensation !== this.snapped.compensation) {
      this.snapped = next;
      this.scale.set(next.compensation);
    }
    // Only a change of face costs a redraw.
    if (face !== this.textStyle.face) {
      this.textStyle = { ...this.textStyle, face };
      this.redraw();
    }
  }

  /** The fit box in device pixels under `deviceScale`, or none. */
  private fitDevice(deviceScale: number): number | undefined {
    if (this.fitHeight === undefined || !(this.fitHeight > 0)) return undefined;
    return this.fitHeight * (deviceScale > 0 ? deviceScale : 1);
  }

  /** The face actually stamped, after the floor. */
  get face(): FaceId {
    return this.textStyle.face ?? "hud";
  }

  /** Whole device pixels each glyph texel is worth. Never fractional. */
  get magnification(): number {
    return this.snapped.magnification;
  }

  /** Drawn width in the parent's units - keyline and compensation included. */
  get contentWidth(): number {
    return this.boxW * this.snapped.compensation;
  }

  /**
   * Line height in the parent's units, keyline included. A descender may draw
   * below this; nothing should be positioned off the descender.
   */
  get contentHeight(): number {
    return this.boxH * this.snapped.compensation;
  }

  private redraw(): void {
    const s = this.pixelScale;
    const layout = layoutBitmapText(this.value, this.textStyle);
    this.boxW = layout.width * s;
    // The LINE box, not the drawn box. A descender - the comma in `1,240`, the
    // tail of a `y` - is ink below the baseline and must not move the label it
    // is in, or two readings in one row would stop sharing a centre the moment
    // one of them crossed a thousand.
    this.boxH = layout.lineHeight * s;
    const g = this.g;
    g.clear();
    if (layout.outline.length > 0) {
      for (const r of layout.outline) g.rect(r.x * s, r.y * s, r.w * s, s);
      g.fill({ color: this.outlineColor, alpha: this.outlineAlpha });
    }
    if (layout.fill.length > 0) {
      for (const r of layout.fill) g.rect(r.x * s, r.y * s, r.w * s, s);
      g.fill({ color: this.fillColor });
    }
    this.place();
  }

  private place(): void {
    this.g.position.set(
      anchorOffset(this.boxW, this.anchorX),
      anchorOffset(this.boxH, this.anchorY),
    );
  }
}

/**
 * Drawn size of `text` in a label of this style, without building one. Sizes are
 * in the parent's units, so `deviceScale` belongs here too: a call site that
 * lays out against a measurement and then draws the label has to be told the
 * same size twice.
 */
export function measureBitmapLabel(
  text: string, scale = 1, deviceScale = 1, style?: BitmapTextStyle, floor = true, fitHeight?: number,
): { readonly width: number; readonly height: number; readonly descent: number } {
  const fit = fitHeight !== undefined && fitHeight > 0 ? fitHeight * (deviceScale > 0 ? deviceScale : 1) : undefined;
  const { compensation, nominal, magnification } = pinToFloor(snapMagnification(scale, deviceScale), style?.face, floor, fit);
  // Measured in the face the label will be stamped in, floor applied, so a
  // call site that lays out against this and then builds the label gets the
  // box that label actually draws.
  const held: BitmapTextStyle = { ...style, face: heldFace(style?.face, magnification, floor, ambientCapFloor, fit) };
  const m = measureBitmapText(text, held);
  const unit = nominal * compensation;
  // `height` is the line box, matching `BitmapLabel.contentHeight`; `descent`
  // is what hangs under it, for a caller that has to leave room below.
  return { width: m.width * unit, height: lineHeightOf(held) * unit, descent: m.descent * unit };
}
